import { create } from 'zustand';
import { MAX_ATTACHMENTS } from '../constants/reports';
import { supabase } from '../lib/supabase';
import { Report, ReportAttachment, ReportableType } from '../types/report';

/** Thrown for issue report validation failures. UI should translate code via issue.validation.<code>. */
export class IssueReportValidationError extends Error {
  constructor(
    public readonly code: string,
    public readonly params?: Record<string, string | number>
  ) {
    super(code);
    this.name = 'IssueReportValidationError';
  }
}

type ReportableWithOptions = Exclude<ReportableType, 'issue'>;

export interface ReportFormData {
  reported_id: string;
  content_type: ReportableWithOptions;
  entity_id: string;
  reason: string;
  admin_notes?: string | null;
  details?: string | null;
}

export interface IssueReportFormData {
  reporter_email: string;
  page_url: string;
  page_path: string;
  issue_title: string;
  issue_description: string;
  files: File[];
}

interface ReportState {
  reports: Report[];
  loading: boolean;
  error: string | null;
  fetchReports: () => Promise<void>;
  createReport: (data: ReportFormData) => Promise<void>;
  submitIssueReport: (data: IssueReportFormData) => Promise<void>;
  updateReportStatus: (
    reportId: string,
    status: Report['status'],
    adminNotes?: string
  ) => Promise<void>;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function uploadIssueAttachment(file: File, accessToken?: string): Promise<ReportAttachment> {
  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-issue-attachment`,
    {
      method: 'POST',
      headers,
      body: formData,
    }
  );

  const result = (await response.json()) as ReportAttachment & { error?: string };
  if (!response.ok) {
    throw new IssueReportValidationError('upload_failed', {
      detail: result.error ?? '',
    });
  }

  return {
    path: result.path,
    file_name: result.file_name,
    size: result.size,
    content_type: result.content_type || null,
  };
}

async function cleanupIssueAttachments(
  attachments: ReportAttachment[],
  accessToken?: string
): Promise<void> {
  if (!accessToken || attachments.length === 0) {
    return;
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  try {
    await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-issue-attachment`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({
        paths: attachments.map(attachment => attachment.path),
      }),
    });
  } catch {
    // Best-effort cleanup; ignore failures
  }
}

export const useReportStore = create<ReportState>((set, get) => ({
  reports: [],
  loading: false,
  error: null,

  fetchReports: async () => {
    set({ loading: true });
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ reports: data as Report[], error: null });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  createReport: async reportData => {
    set({ loading: true });
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { error } = await supabase.from('reports').insert([
        {
          ...reportData,
          reporter_id: user.user.id,
          reporter_email: user.user.email ?? null,
          status: 'pending',
          created_at: new Date().toISOString(),
        },
      ]);

      if (error) throw error;
      await get().fetchReports();
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  submitIssueReport: async issueData => {
    set({ loading: true, error: null });
    const attachments: ReportAttachment[] = [];
    let accessToken: string | undefined;

    try {
      if (issueData.files.length > MAX_ATTACHMENTS) {
        throw new IssueReportValidationError('max_attachments', { max: MAX_ATTACHMENTS });
      }

      for (const file of issueData.files) {
        if (!file.type.startsWith('image/')) {
          throw new IssueReportValidationError('images_only');
        }
        if (file.size > MAX_FILE_SIZE) {
          throw new IssueReportValidationError('file_size_max');
        }
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const {
        data: { session },
      } = await supabase.auth.getSession();

      accessToken = session?.access_token ?? undefined;

      const hasUserEmail = Boolean(user?.email?.trim());
      const reporterEmail = (hasUserEmail ? user!.email!.trim() : issueData.reporter_email).trim();
      if (!EMAIL_REGEX.test(reporterEmail)) {
        throw new IssueReportValidationError('invalid_email');
      }

      if (!issueData.issue_title.trim()) {
        throw new IssueReportValidationError('title_required');
      }

      if (!issueData.issue_description.trim()) {
        throw new IssueReportValidationError('description_required');
      }

      const filesToUpload = accessToken ? issueData.files : [];

      for (const file of filesToUpload) {
        const uploaded = await uploadIssueAttachment(file, accessToken);
        attachments.push(uploaded);
      }

      const { error } = await supabase.from('reports').insert([
        {
          reporter_id: user?.id ?? null,
          reporter_email: reporterEmail,
          reported_id: null,
          content_type: 'issue',
          entity_id: null,
          reason: 'product_bug',
          details: issueData.issue_description.trim(),
          status: 'pending',
          page_url: issueData.page_url.trim(),
          page_path: issueData.page_path.trim(),
          issue_title: issueData.issue_title.trim(),
          issue_description: issueData.issue_description.trim(),
          attachments,
          created_at: new Date().toISOString(),
        },
      ]);

      if (error) throw error;
    } catch (error) {
      await cleanupIssueAttachments(attachments, accessToken);
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateReportStatus: async (reportId: string, status: Report['status'], adminNotes?: string) => {
    set({ loading: true });
    try {
      const { error } = await supabase
        .from('reports')
        .update({
          status,
          admin_notes: adminNotes,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', reportId);

      if (error) throw error;
      await get().fetchReports();
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },
}));
